import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API, { fmt } from '../utils/api';

const ROLE_LABEL = {
  admin: 'مدير النظام', sales_manager: 'مدير مبيعات',
  sales_employee: 'موظف مبيعات', warehouse: 'مسؤول المخزن',
  production_manager: 'مسؤول الإنتاج',
};

function StatCard({ label, value, icon, accent, sub }) {
  return (
    <div className="stat-card">
      <div className="stat-card-accent" style={{ background: accent || 'var(--brand)' }} />
      <div className="stat-icon" style={{ background: accent ? accent + '18' : 'var(--brand-light)' }}>
        <span style={{ fontSize:'1.15rem' }}>{icon}</span>
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-trend" style={{ color: 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    API.get('/dashboard').then(r => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return (
    <div className="loading">
      <div className="spinner" />
      <span>جاري التحميل...</span>
    </div>
  );

  const { stats, recent_orders, low_stock, top_debtors, today_tasks } = data;
  const role = user?.role;
  const isManager  = ['admin','sales_manager'].includes(role);
  const isSales    = ['admin','sales_manager','sales_employee'].includes(role);
  const isWarehouse = role === 'warehouse';

  const today = new Date();
  const greeting = today.getHours() < 12 ? 'صباح الخير' : today.getHours() < 18 ? 'مساء الخير' : 'مساء الخير';

  const statCards = [
    isSales && {
      label: 'إجمالي العملاء', value: stats.total_customers,
      icon: '👥', accent: 'var(--brand)',
    },
    isSales && {
      label: 'إجمالي الطلبات', value: stats.total_orders,
      icon: '🛒', accent: 'var(--info)',
    },
    isSales && {
      label: 'مبيعات اليوم', value: fmt.currency(stats.today_sales),
      icon: '💵', accent: 'var(--success)',
    },
    isManager && {
      label: 'إجمالي الديون', value: fmt.currency(stats.total_debt),
      icon: '📊', accent: stats.total_debt > 0 ? 'var(--danger)' : 'var(--success)',
    },
    (isManager || isWarehouse) && {
      label: 'مخزون منخفض', value: stats.low_stock_count,
      icon: '📦', accent: stats.low_stock_count > 0 ? 'var(--danger)' : 'var(--success)',
    },
    isManager && {
      label: 'طلبات متأخرة', value: stats.overdue_orders,
      icon: '⏰', accent: stats.overdue_orders > 0 ? 'var(--danger)' : 'var(--success)',
    },
    (isManager || role === 'production_manager') && {
      label: 'قيد الإنتاج', value: stats.production_pending,
      icon: '🏭', accent: 'var(--warning)',
    },
    {
      label: 'مهامي المعلقة', value: stats.my_tasks,
      icon: '📋', accent: stats.my_tasks > 0 ? 'var(--warning)' : 'var(--success)',
    },
  ].filter(Boolean);

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">لوحة التحكم</h1>
          <div className="page-title-sub">{greeting}، {user?.name} — {ROLE_LABEL[role]}</div>
        </div>
        <div style={{ color:'var(--muted)', fontSize:'0.82rem', textAlign:'left', direction:'ltr' }}>
          {today.toLocaleDateString('ar-SA', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        {statCards.map((s, i) => <StatCard key={i} {...s} />)}
      </div>

      {/* Widgets */}
      <div className="dashboard-grid">

        {/* مهام اليوم */}
        {today_tasks?.length > 0 && (
          <div className="widget">
            <div className="widget-header">
              <span className="widget-title">📋 مهام اليوم</span>
              <Link to="/tasks" className="btn btn-sm btn-ghost">عرض الكل</Link>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
              {today_tasks.map(t => (
                <div key={t.id} style={{
                  display:'flex', alignItems:'flex-start', justifyContent:'space-between',
                  padding:'10px 0', borderBottom:'1px solid var(--border-2)', gap:10
                }}>
                  <div style={{ minWidth:0 }}>
                    <div className="font-bold truncate" style={{ fontSize:'0.875rem' }}>{t.title}</div>
                    {t.customer_name && <div className="text-muted" style={{ fontSize:'0.78rem', marginTop:2 }}>{t.customer_name}</div>}
                  </div>
                  <span style={{
                    fontSize:'0.73rem', fontWeight:700, whiteSpace:'nowrap',
                    color: new Date(t.due_date) < new Date() ? 'var(--danger)' : 'var(--muted)',
                    background: new Date(t.due_date) < new Date() ? 'var(--danger-bg)' : 'var(--surface-3)',
                    padding:'2px 8px', borderRadius:20, flexShrink:0
                  }}>
                    {fmt.date(t.due_date) || 'اليوم'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* آخر الطلبات */}
        {isSales && recent_orders?.length > 0 && (
          <div className="widget">
            <div className="widget-header">
              <span className="widget-title">🛒 آخر الطلبات</span>
              <Link to="/orders" className="btn btn-sm btn-ghost">عرض الكل</Link>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr><th>العميل</th><th>الإجمالي</th><th>التاريخ</th></tr>
                </thead>
                <tbody>
                  {recent_orders.map(o => (
                    <tr key={o.id}>
                      <td className="font-bold" style={{ fontSize:'0.85rem' }}>{o.customer_name}</td>
                      <td className="text-success font-bold">{fmt.currency(o.total)}</td>
                      <td className="text-muted" style={{ fontSize:'0.8rem' }}>{fmt.date(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* أكبر المدينين */}
        {isManager && top_debtors?.length > 0 && (
          <div className="widget">
            <div className="widget-header">
              <span className="widget-title">⚠️ أكبر المدينين</span>
              <Link to="/customers" className="btn btn-sm btn-ghost">العملاء</Link>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
              {top_debtors.map((c, i) => (
                <div key={c.id} style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'10px 0', borderBottom:'1px solid var(--border-2)'
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                    <div style={{
                      width:28, height:28, borderRadius:8, flexShrink:0,
                      background: i === 0 ? 'var(--danger-bg)' : 'var(--surface-3)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'0.72rem', fontWeight:800,
                      color: i === 0 ? 'var(--danger)' : 'var(--muted)'
                    }}>{i + 1}</div>
                    <div style={{ minWidth:0 }}>
                      <div className="font-bold truncate" style={{ fontSize:'0.875rem' }}>{c.name}</div>
                      <div className="text-muted" style={{ fontSize:'0.78rem' }}>{c.phone}</div>
                    </div>
                  </div>
                  <span className="font-bold text-danger" style={{ fontSize:'0.875rem', whiteSpace:'nowrap' }}>
                    {fmt.currency(c.debt)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* مخزون منخفض */}
        {(isManager || isWarehouse) && low_stock?.length > 0 && (
          <div className="widget">
            <div className="widget-header">
              <span className="widget-title">📦 مخزون منخفض</span>
              <Link to="/inventory" className="btn btn-sm btn-ghost">المخزون</Link>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
              {low_stock.map(p => (
                <div key={p.id} style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'9px 0', borderBottom:'1px solid var(--border-2)'
                }}>
                  <div style={{ minWidth:0 }}>
                    <div className="font-bold truncate" style={{ fontSize:'0.875rem' }}>{p.name}</div>
                    <div className="text-muted" style={{ fontSize:'0.78rem' }}>{p.category_name}</div>
                  </div>
                  <div style={{ textAlign:'left', flexShrink:0 }}>
                    <span className="badge badge-danger">{p.current_qty} {p.unit}</span>
                    <div style={{ fontSize:'0.7rem', color:'var(--muted)', marginTop:2, textAlign:'center' }}>
                      حد: {p.min_stock}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
